/**
 * Hero background RENDERER — the React layer that turns a typed, rules-checked spec
 * into rendered `background` JSX. The pure rules, types, preset menu, and legal-space
 * enumerator live in ./hero-background-spec (importable on a backend with no React).
 *
 * - `heroBackground(spec)` is the sanctioned way to set a hero `background` —
 *   never hand-write `background={<GridDashedTop />}` or stack components yourself.
 * - This file is the only place that imports background components.
 *
 * Everything from ./hero-background-spec is re-exported here so existing
 * `@mdxui/neo` barrel consumers keep importing from one place.
 */
import type { ComponentType, ReactNode } from 'react'
import {
  GridDashedTop,
  GridDashedCenter,
  GridDashedBottom,
  GridDiagonalTop,
  GridDiagonalCenter,
  GridSparseTop,
  GridSparseCenter,
  GridCrosshatchTop,
  GridCrosshatchCenter,
  GridPerspectiveTop,
  GridPerspectiveBottom,
  GlowTop,
  GlowCenter,
  GlowBottom,
  GradientBlur,
  SpotlightTopLeft,
  SpotlightTopRight,
  Beams,
  BeamsTop,
  NoiseTexture,
} from '../backgrounds'
import { heroBackgroundLayers, type ComponentName, type SectionBackground } from './hero-background-spec'

// The pure spec (types, rules, presets, enumerator) is part of this module's public
// surface — re-export so `@mdxui/neo` consumers don't need to know about the split.
export * from './hero-background-spec'

// ─── Renderer ──────────────────────────────────────────────────────────────────

// `satisfies` checks REGISTRY covers EVERY ComponentName (and nothing extra) without
// widening each component's prop type — mirrors the original `as const` + use-site cast.
const REGISTRY = {
  GridDashedTop,
  GridDashedCenter,
  GridDashedBottom,
  GridDiagonalTop,
  GridDiagonalCenter,
  GridSparseTop,
  GridSparseCenter,
  GridCrosshatchTop,
  GridCrosshatchCenter,
  GridPerspectiveTop,
  GridPerspectiveBottom,
  GlowTop,
  GlowCenter,
  GlowBottom,
  GradientBlur,
  SpotlightTopLeft,
  SpotlightTopRight,
  Beams,
  BeamsTop,
} satisfies Record<ComponentName, ComponentType<never>>

/** THE hero-background entry point. Pass its result to a hero's `background` prop. */
export function heroBackground(bg: SectionBackground): ReactNode {
  return (
    <>
      {heroBackgroundLayers(bg).map(({ name, props }, i) => {
        const C = REGISTRY[name] as ComponentType<Record<string, unknown>>
        return <C key={i} {...props} />
      })}
    </>
  )
}

/** Page-level grain. Render once at the layout root, NOT inside a hero (R6). */
export function pageNoise(opts: { fixed?: boolean } = {}): ReactNode {
  return <NoiseTexture fixed={opts.fixed ?? true} />
}
